import { Action, AnyAction, Dispatch } from 'redux';

import { API_DEFAULT_RETRIES, COLLECTIONS } from 'appConstants';
import { RequestAction } from 'reducers';
import {
  ApiRequest,
  ImmutableQuery,
  MiscUtils,
  NewTicketUtils,
} from 'services';
import { SentryManualError } from 'types';

/**
 * CollectionsManager
 *
 * The CollectionsManager is a reducer that allows for storing a collection on
 * the Redux global state so that any connected component may access the items
 * within the collection as well as be updated with any changes to the
 * collection. It is also possible to imperitively retrive a collection via the
 * Redux store.getState().Collections[<id>], with the appropriate Collection ID.
 *
 * A collection is an array of object which may be filtered or paginated. A
 * collection is typically used as a logical interface to an API call,
 * encapsulating server-side pagination and filtering. It also provides the
 * same capability for local collections, via a unified interface.
 */

/* Types */

export interface CollectionPagination {
  itemsPerPage?: number;
  page?: number;
  maxLimit?: number;
}

interface LocalCollectionFilter<T> {
  method: (item: T) => string;
  param: string;
}

export interface LocalCollectionOptions<T> {
  readonly filter?: LocalCollectionFilter<T>;
  readonly items: T[];
  readonly onUpdate?: (isSuccess?: boolean) => void;
  readonly pagination?: CollectionPagination;
}

export interface ResourceCollectionOptions<T> {
  readonly dataKey?: string;
  readonly filter?: ImmutableQuery;
  readonly onUpdate?: (isSuccess?: boolean) => void;
  readonly pagination?: CollectionPagination;
  readonly preRequest?: (query: ImmutableQuery) => ImmutableQuery;
  readonly resourceUrl: string;
  readonly retries?: number;
  readonly usePost?: boolean;
}

interface CollectionsAction<T> extends Action {
  readonly key: String;
  readonly options?: LocalCollectionOptions<T> | ResourceCollectionOptions<T>;
}

interface BaseCollection<T> {
  readonly id: string;
  readonly isResource: boolean;
  readonly onUpdate: (isSuccess?: boolean) => void;
  readonly pagination?: CollectionPagination;
  readonly total: number;
  readonly view: T[];
}

export interface ResourceCollection<T> extends BaseCollection<T> {
  readonly dataKey: string;
  readonly filter: ImmutableQuery;
  readonly preRequest: (query: ImmutableQuery) => ImmutableQuery;
  readonly resourceUrl: string;
  readonly retries: number;
}

export interface LocalCollection<T> extends BaseCollection<T> {
  readonly filter: LocalCollectionFilter<T>;
  readonly items: T[];
}

export type Collection<T> = ResourceCollection<T> | LocalCollection<T>;

export type CollectionsManager = {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  [key: string]: ResourceCollection<any> | LocalCollection<any>;
};

/* Type Guards */

function isCollectionId<T>(
  collection: Collection<T> | string,
): collection is string {
  return typeof collection === 'string';
}

function isResourceOptions<T>(
  options: LocalCollectionOptions<T> | ResourceCollectionOptions<T>,
): options is ResourceCollectionOptions<T> {
  return (options as ResourceCollectionOptions<T>).resourceUrl !== undefined;
}

function isResourceCollection<T>(
  collection: LocalCollection<T> | ResourceCollection<T>,
): collection is ResourceCollection<T> {
  return (collection as ResourceCollection<T>).resourceUrl !== undefined;
}

function isLocalOptions<T>(
  options: LocalCollectionOptions<T> | ResourceCollectionOptions<T>,
): options is LocalCollectionOptions<T> {
  return (options as LocalCollectionOptions<T>).items !== undefined;
}

function isLocalCollection<T>(
  collection: LocalCollection<T> | ResourceCollection<T>,
): collection is LocalCollection<T> {
  return (collection as LocalCollection<T>).items !== undefined;
}

/* Initial State */

// Initialize all collections to a default state
const initialState: CollectionsManager = Object.keys(COLLECTIONS).reduce(
  (acc, id) => {
    acc[id] = { view: [] };
    /**
     * Initially we need to set the default max limit to same
     * as the default page size.
     */
    acc[id].pagination = { maxLimit: 25 };
    return acc;
  },
  {},
);

/* Local Utils */

/** Convert collection pagination to query strings */
const paginationToQuery = ({ page, itemsPerPage }: CollectionPagination) =>
  page && itemsPerPage
    ? {
        offset: itemsPerPage * (page - 1),
        limit: itemsPerPage,
      }
    : {};

/* Actions */

enum Actions {
  CLEAR_COLLECTION = 'CLEAR_COLLECTION',
  SET_COLLECTION_SELECTION_LIMIT = 'SET_COLLECTION_SELECTION_LIMIT',
  SET_COLLECTION_PAGINATION = 'SET_COLLECTION_PAGINATION',
  SET_LOCAL_COLLECTION = 'SET_LOCAL_COLLECTION',
  SET_RESOURCE_COLLECTION = 'SET_RESOURCE_COLLECTION',
  UPDATE_RESOURCE_COLLECTION_SINGLE_PAGE = 'UPDATE_RESOURCE_COLLECTION_SINGLE_PAGE',
  UPDATE_RESOURCE_COLLECTION_LOCALLY = 'UPDATE_RESOURCE_COLLECTION_LOCALLY',
}

const clearCollection = <T>(key: string): CollectionsAction<T> => ({
  type: Actions.CLEAR_COLLECTION,
  key,
});

const setLocalCollection = <T>(
  key: string,
  items: T[],
  view: T[],
  filter: LocalCollectionFilter<T>,
  onUpdate: (isSuccess?: boolean) => void,
  pagination?: CollectionPagination,
  total?: number,
) => ({
  type: Actions.SET_LOCAL_COLLECTION,
  filter,
  key,
  isResource: false,
  items,
  onUpdate,
  pagination,
  total: total || total === 0 ? total : items.length,
  view,
});

const setResourceCollection = <T>(
  key: string,
  resourceUrl: string,
  response: { total?: number },
  dataKey: string,
  filter: ImmutableQuery,
  onUpdate: (isSuccess?: boolean) => void,
  preRequest: (query: ImmutableQuery) => ImmutableQuery,
  pagination?: CollectionPagination,
  retries?: number,
  usePost?: boolean,
) => ({
  type: Actions.SET_RESOURCE_COLLECTION,
  dataKey,
  filter,
  isResource: true,
  key,
  onUpdate,
  pagination,
  preRequest,
  resourceUrl,
  retries: retries === undefined ? API_DEFAULT_RETRIES : retries,
  total: dataKey ? response.total : (response as T[]).length,
  usePost: usePost || false,
  view: dataKey ? MiscUtils.get(response, dataKey) || [] : response || [],
});

const setResourceCollectionSinglePage = <T>(
  response: { total?: number },
  key: string,
) => ({
  type: Actions.UPDATE_RESOURCE_COLLECTION_SINGLE_PAGE,
  response: response,
  key: key,
});

const setCollectionSelectionLimit = (key: string, maxLimit: number) => ({
  type: Actions.SET_COLLECTION_SELECTION_LIMIT,
  key,
  maxLimit,
});

const updateResourceCollectionLocally = <T>(view: T, key: string) => ({
  type: Actions.UPDATE_RESOURCE_COLLECTION_LOCALLY,
  key,
  view,
});

/* Reducer */

const collectionsManagerReducer = (state = initialState, action: AnyAction) => {
  const { key, ...rest } = action;
  const collection = state[key];

  switch (action.type) {
    case Actions.CLEAR_COLLECTION:
      return {
        ...state,
        [key]: {
          ...collection,
          view: [],
          total: 0,
        },
      };
    case Actions.UPDATE_RESOURCE_COLLECTION_LOCALLY:
    case Actions.SET_RESOURCE_COLLECTION:
      return {
        ...state,
        [key]: {
          ...collection,
          ...rest,
          id: key,
        },
      };
    case Actions.UPDATE_RESOURCE_COLLECTION_SINGLE_PAGE:
      return {
        ...state,
        [key]: {
          view: [...state[key].view, ...action.response.resources],
          ...rest,
          id: key,
        },
      };
    case Actions.SET_LOCAL_COLLECTION:
      return {
        ...state,
        [key]: {
          id: key,
          ...rest,
        },
      };
    case Actions.SET_COLLECTION_PAGINATION:
      const { page, itemsPerPage } = action;
      return {
        ...state,
        [key]: {
          ...collection,
          pagination: {
            ...collection.pagination,
            page,
            itemsPerPage,
          },
        },
      };
    case Actions.SET_COLLECTION_SELECTION_LIMIT:
      const { maxLimit } = action;
      return {
        ...state,
        [key]: {
          ...collection,
          pagination: {
            ...collection.pagination,
            maxLimit,
          },
        },
      };
    default:
      return state;
  }
};

const defaultLocalCollectionOptions = {
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  filter: { method: (item: any) => '', param: '' },
  onUpdate: (isSuccess?: boolean) => null,
  pagination: {
    itemsPerPage: 25,
    page: 1,
    maxLimit: 25,
  },
};

const evaluateLocalCollection = async <T>(
  key: string,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  options: LocalCollectionOptions<any>,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  dispatch: Dispatch<CollectionsAction<any>>,
) => {
  const { filter, items, onUpdate } = {
    ...defaultLocalCollectionOptions,
    ...options,
  };
  let view = [...items];

  // Evaluate Filter Options
  if (filter.param !== '') {
    view = view.filter((item: T) => filter.method(item).includes(filter.param));
  }
  const total = view.length;

  // Evaluate Pagination Options
  const pagination = options.pagination
    ? {
        ...defaultLocalCollectionOptions.pagination,
        ...options.pagination,
      }
    : undefined;

  if (pagination) {
    const startIndex = pagination.itemsPerPage * (pagination.page - 1);
    const endIndex = Math.min(
      startIndex + pagination.itemsPerPage,
      view.length,
    );
    view = view.slice(startIndex, endIndex);
  }

  dispatch(
    setLocalCollection(key, items, view, filter, onUpdate, pagination, total),
  );
  onUpdate(true);
};

const defaultResourceOptions = {
  dataKey: '',
  filter: new ImmutableQuery(),
  onUpdate: (isSuccess?: boolean) => null,
  preRequest: (value: ImmutableQuery) => value,
  resourceUrl: '',
};

const requestResource = async (
  key: string,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  options: ResourceCollectionOptions<any>,
  /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
  dispatch: Dispatch<CollectionsAction<any>>,
) => {
  const {
    dataKey,
    filter,
    onUpdate,
    pagination,
    preRequest,
    resourceUrl,
    retries = API_DEFAULT_RETRIES,
    usePost,
  } = {
    ...defaultResourceOptions,
    ...options,
  };

  // Compose filter and pagination
  const query = pagination ? filter.set(paginationToQuery(pagination)) : filter;

  // Fetch data
  const response = await (usePost
    ? ApiRequest.post(resourceUrl, query, { dispatchId: key, retries })
    : ApiRequest.get(resourceUrl + preRequest(query).toQueryString(), {
        dispatchId: key,
        retries,
      }));

  /**
   * Discussed with CTAPI team, only log empty response
   * for TEMPLATE collection
   */
  if (key === COLLECTIONS.TEMPLATE && response) {
    // If the collection is empty log it to Sentry
    const isDataEmpty = dataKey ? response.total : response.length;
    if (isDataEmpty === 0) {
      MiscUtils.logSentryErrorManually({
        name: 'No Collection Data',
        description: `Empty data for ${key} collection`,
        extraData: {
          collectionType: key,
          url: resourceUrl,
          payload: JSON.stringify(query),
        },
        fingerprint: [
          window.PORTAL_DATA.userId,
          MiscUtils.isUserImpersonated() ? 'customer' : 'racker',
          resourceUrl,
          JSON.stringify(query),
        ],
        tags: {
          userId: window.PORTAL_DATA.userId,
          domainId: window.PORTAL_DATA.domainId,
          isUserImpersonated: MiscUtils.isUserImpersonated(),
        },
      } as SentryManualError);
    }
  }

  dispatch(
    setResourceCollection(
      key,
      resourceUrl,
      response || {},
      dataKey,
      filter,
      onUpdate,
      preRequest,
      pagination,
      retries,
      usePost,
    ),
  );

  if (
    NewTicketUtils.isWhiteListedCustomer() &&
    key === COLLECTIONS.DEVICE &&
    response
  ) {
    const { _meta } = response;
    const { totalPages } = _meta;

    let PagesArray: number[] = Array.from(
      { length: totalPages },
      (_, index) => index + 1,
    ).slice(1);
    const updatedResourcesResponse = await Promise.all(
      PagesArray.map((pageNum: number) => {
        let num = 500;
        if (pageNum > 2) {
          num = num * (pageNum - 1);
        }
        return ApiRequest.get(
          resourceUrl + preRequest(query).toQueryString() + `&offset=${num}`,
          {
            dispatchId: key,
            retries,
          },
        );
      }),
    );
    updatedResourcesResponse.forEach((resourceResponse) =>
      dispatch(
        setResourceCollectionSinglePage(resourceResponse, COLLECTIONS.DEVICE),
      ),
    );
  }

  onUpdate(!ApiRequest.isError(response));
};

/* Thunks */
const createCollection = <T>(
  key: string,
  options: LocalCollectionOptions<T> | ResourceCollectionOptions<T>,
) => (dispatch: Dispatch<CollectionsAction<T>>) => {
  if (isResourceOptions<T>(options)) {
    return requestResource(key, options, dispatch);
  }

  if (isLocalOptions<T>(options)) {
    return evaluateLocalCollection(key, options, dispatch);
  }

  return;
};

const filterCollection = <T>(
  collectionOrId: Collection<T> | string,
  query: ImmutableQuery | { [key: string]: string[] | string | null } | string,
) => (dispatch: Dispatch<CollectionsAction<T>>, getState: Function) => {
  const collection = isCollectionId<T>(collectionOrId)
    ? getState().Collections[collectionOrId]
    : collectionOrId;
  const pagination = collection.pagination || {};

  // reset pagination to first page
  if ('page' in pagination) {
    pagination.page = 1;
  }

  if (isResourceCollection<T>(collection)) {
    const filter = collection.filter.set(query);
    const options = { ...collection, filter, pagination };
    return requestResource(collection.id, options, dispatch);
  }

  // Local collection filter only supports single string param
  let param = '';

  if (typeof query === 'string') {
    param = query;
  } else if (query instanceof ImmutableQuery) {
    param = query.get('text');
  } else if ('text' in query && !Array.isArray(query.text)) {
    param = query.text || '';
  }

  if (isLocalCollection<T>(collection)) {
    const filter = { ...collection.filter, param: param.toLowerCase() };
    const options = { ...collection, filter, pagination };
    return evaluateLocalCollection(collection.id, options, dispatch);
  }

  return;
};

const paginateCollection = <T>(
  collectionOrId: Collection<T> | string,
  page: number,
  itemsPerPage: number,
) => (dispatch: Dispatch<CollectionsAction<T>>, getState: Function) => {
  const collection = isCollectionId<T>(collectionOrId)
    ? getState().Collections[collectionOrId]
    : collectionOrId;
  const pagination = { itemsPerPage, page };
  const options = {
    ...collection,
    pagination: {
      ...collection.pagination,
      ...pagination,
    },
  };

  if (isResourceCollection<T>(collection)) {
    return requestResource(collection.id, options, dispatch);
  }

  if (isLocalCollection<T>(collection)) {
    return evaluateLocalCollection(collection.id, options, dispatch);
  }

  return;
};

const refreshCollection = <T>(collectionOrId: Collection<T> | string) => (
  dispatch: Dispatch<CollectionsAction<T>>,
  getState: Function,
) => {
  const collection = isCollectionId<T>(collectionOrId)
    ? getState().Collections[collectionOrId]
    : collectionOrId;

  if (isResourceCollection<T>(collection)) {
    return requestResource(collection.id, collection, dispatch);
  }

  return {} as Promise<RequestAction>;
};

/** Only updates the Collections Filter */
const setCollectionFilter = <T>(
  collectionOrId: Collection<T> | string,
  query: { [key: string]: string | null } | string,
) => (dispatch: Dispatch<CollectionsAction<T>>, getState: Function) => {
  const collection = isCollectionId<T>(collectionOrId)
    ? getState().Collections[collectionOrId]
    : collectionOrId;

  const pagination = collection.pagination || {};

  // reset pagination to first page
  if ('page' in pagination) {
    pagination.page = 1;
  }

  if (isResourceCollection<T>(collection)) {
    const filter = collection.filter.set(query);
    dispatch(
      setResourceCollection(
        collection.id,
        collection.resourceUrl,
        {},
        collection.dataKey,
        filter,
        collection.onUpdate,
        collection.preRequest,
        pagination,
        0,
      ),
    );
  }

  return;
};

export {
  clearCollection,
  collectionsManagerReducer,
  createCollection,
  filterCollection,
  paginateCollection,
  refreshCollection,
  setCollectionFilter,
  setCollectionSelectionLimit,
  updateResourceCollectionLocally,
};
